import SearchIcon from "@/assets/Search";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebouncedSearchParam } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Files,
  Landmark,
  Lock,
  Plus,
  UserRound,
  FilterIcon,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import CompletedIcon from "@/assets/completed.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useSearchParams } from "react-router-dom";
import {
  CompanyRole,
  ProjectCategoryType,
  ProjectFiltersQuery,
  ProjectFiltersQueryVariables,
} from "@/__generated__/graphql";
import { addDays } from "date-fns";
import { PROJECT_FILTER_QUERY } from "@/graphql/queries/common/ProjectsQuery";
import { useQuery } from "@apollo/client";
import { ProjectIcon } from "@/helpers/icons";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import { useUserContext } from "@/contexts/UserContextProvider";
import { isAdminOrCollaborator } from "@/helpers/userContext";
import { intersection } from "lodash";
import * as Switch from "@radix-ui/react-switch";
import { Checkbox } from "@/components/ui/checkbox";

export default function ProjectFiltersContainer() {
  const { currentUser } = useUserContext();
  const { data, loading } = useQuery<
    ProjectFiltersQuery,
    ProjectFiltersQueryVariables
  >(PROJECT_FILTER_QUERY);

  if (loading && !data) {
    return null;
  }

  if (data)
    return (
      <div className="flex items-center gap-3">
        <SearchBar />

        {!currentUser?.canBeAssignedTasks && <Filter data={data} />}
      </div>
    );
}

function Filter({ data }: { data: ProjectFiltersQuery }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isHotspringVendorProfile, currentUser, role } = useUserContext();

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isAddFiltersOpen, setIsAddFiltersOpen] = useState(false);

  const [showArchived, setShowArchived] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [projectType, setProjectType] = useState<ProjectCategoryType | null>(
    null,
  );
  const [startDate, setStartDate] = useState<
    keyof typeof CreatedFilterOptions | null
  >(null);
  const [createdBy, setCreatedBy] = useState<string | null>(null);

  const [officeIdSelected, setOfficeIdSelected] = useState<string[]>([]);
  const [showOfficeFilter, setShowOfficeFilter] = useState<boolean>(false);
  const [officeSearch, setOfficeSearch] = useState("");
  const [creatorSearch, setCreatorSearch] = useState("");

  const filtersCount = useMemo(() => {
    return intersection(
      Array.from(searchParams.keys()),
      Object.values(ProjectFilterOptions),
    ).length;
  }, [searchParams]);

  const applyFilters = () => {
    setIsFilterModalOpen(false);
    setSearchParams((params) => {
      if (showArchived) params.set(ProjectFilterOptions.SHOW_ARCHIVED, "true");
      else params.delete(ProjectFilterOptions.SHOW_ARCHIVED);

      if (showCompleted)
        params.set(ProjectFilterOptions.SHOW_COMPLETED, "true");
      else params.delete(ProjectFilterOptions.SHOW_COMPLETED);

      if (showUnassigned)
        params.set(ProjectFilterOptions.SHOW_UNASSIGNED, "true");
      else params.delete(ProjectFilterOptions.SHOW_UNASSIGNED);

      if (projectType)
        params.set(ProjectFilterOptions.PROJECT_TYPE, projectType);
      else params.delete(ProjectFilterOptions.PROJECT_TYPE);

      if (startDate) params.set(ProjectFilterOptions.START_DATE, startDate);
      else params.delete(ProjectFilterOptions.START_DATE);

      if (createdBy) params.set(ProjectFilterOptions.CREATED_BY, createdBy);
      else params.delete(ProjectFilterOptions.CREATED_BY);

      if (officeIdSelected.length > 0)
        params.set(ProjectFilterOptions.OFFICE_ID, officeIdSelected.join(","));
      else params.delete(ProjectFilterOptions.OFFICE_ID);

      return params;
    });
  };

  const initFilters = useCallback(() => {
    setShowArchived(!!searchParams.get(ProjectFilterOptions.SHOW_ARCHIVED));
    setShowCompleted(!!searchParams.get(ProjectFilterOptions.SHOW_COMPLETED));
    setShowUnassigned(!!searchParams.get(ProjectFilterOptions.SHOW_UNASSIGNED));
    setProjectType(
      searchParams.get(
        ProjectFilterOptions.PROJECT_TYPE,
      ) as ProjectCategoryType,
    );
    setStartDate(
      searchParams.get(
        ProjectFilterOptions.START_DATE,
      ) as keyof typeof CreatedFilterOptions,
    );
    setCreatedBy(searchParams.get(ProjectFilterOptions.CREATED_BY));
    const officeIds = searchParams.get(ProjectFilterOptions.OFFICE_ID);
    setOfficeIdSelected(officeIds ? officeIds.split(",").filter(Boolean) : []);
    setShowOfficeFilter(!!officeIds);
  }, [searchParams]);

  const resetFilters = () => {
    setSearchParams((params) => {
      Object.values(ProjectFilterOptions).forEach((value) => {
        params.delete(value);
      });
      return params;
    });
    initFilters();
  };

  useEffect(() => {
    initFilters();
  }, [initFilters, isFilterModalOpen]);

  return (
    <Popover open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn("h-[38px] rounded border text-secondary-foreground", {
            "text-primary-foreground bg-tertiary": isFilterModalOpen,
            "text-service-paint border-service-paint border-2 bg-primary hover:bg-primary relative":
              filtersCount > 0,
          })}
        >
          <FilterIcon
            color={
              filtersCount > 0
                ? "var(--color-service-paint)"
                : isFilterModalOpen
                  ? "var(--color-primary-foreground)"
                  : "var(--color-secondary-foreground"
            }
          />
          Filter {filtersCount > 0 && `(${filtersCount})`}
          {filtersCount > 0 && (
            <>
              <div className="h-1 w-3" /> {/* placeholder for X */}
              <span
                className="absolute top-1/2 -translate-y-1/2 right-2 z-10 rounded-full pointer-events-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  resetFilters();
                }}
              >
                <X className="text-service-paint" />
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-fit p-0 min-w-140" align="end">
        <div className="p-4 text-sm">
          <div className="flex items-center justify-between">
            <div className="font-medium">Filter projects</div>

            <div
              className="text-xs text-secondary-foreground cursor-pointer"
              onClick={resetFilters}
            >
              Clear
            </div>
          </div>

          <div className="mt-4 text-secondary-foreground">
            <div className="mb-2 text-[10px] font-medium ">Quick filters</div>

            <div className="flex items-center gap-3">
              <Button
                className={cn(
                  "bg-transparent text-secondary-foreground border text-xs font-normal",
                  {
                    "bg-service-assets text-white hover:bg-service-assets/96":
                      showCompleted,
                  },
                )}
                onClick={() => setShowCompleted(!showCompleted)}
              >
                <img
                  className={cn(
                    "size-3 transition invert group-hover:invert-0 group-hover:brightness-1",
                    "dark:invert-0 dark:group-hover:invert dark:group-hover:brightness-0",
                    showCompleted && "invert dark:invert dark:brightness-0",
                  )}
                  src={CompletedIcon}
                  alt="icon"
                />
                Completed
              </Button>

              <Button
                className={cn(
                  "bg-transparent text-secondary-foreground border text-xs font-normal",
                  {
                    "bg-service-assets text-white hover:bg-service-assets/96":
                      showUnassigned,
                  },
                )}
                onClick={() => setShowUnassigned(!showUnassigned)}
              >
                <UserRound className="size-3" />
                Unassigned
              </Button>
            </div>

            <div className="mt-5 flex flex-col gap-2">
              <div className="text-[10px]">Other filters</div>

              {projectType && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-2 w-30">
                    <Files className="rotate-y-180" size={14} />
                    Project Type
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                        <div className="flex items-center gap-2">
                          <ProjectIcon category={projectType} />
                          {
                            data.projectCategory.find(
                              (c) => c.type === projectType,
                            )?.name
                          }
                        </div>
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground"
                      style={{
                        width: "var(--radix-dropdown-menu-trigger-width)",
                      }}
                      align="end"
                    >
                      {Object.values(ProjectCategoryType)
                        .filter((t) => t !== projectType)
                        .map((type) => (
                          <DropdownMenuItem
                            key={type}
                            className="cursor-pointer"
                            onClick={() => setProjectType(type)}
                          >
                            <ProjectIcon category={type} />
                            {
                              data.projectCategory.find((c) => c.type === type)
                                ?.name
                            }
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <X
                    className="size-4 cursor-pointer"
                    onClick={() => setProjectType(null)}
                  />
                </div>
              )}

              {startDate && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-2 w-30">
                    <Calendar size={14} />
                    Created
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                        {CreatedFilterOptions[startDate].display}
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground"
                      style={{
                        width: "var(--radix-dropdown-menu-trigger-width)",
                      }}
                      align="end"
                    >
                      {Object.keys(CreatedFilterOptions)
                        .filter((k) => k !== startDate)
                        .map((key) => (
                          <DropdownMenuItem
                            key={key}
                            className="cursor-pointer"
                            onClick={() =>
                              setStartDate(
                                key as keyof typeof CreatedFilterOptions,
                              )
                            }
                          >
                            {
                              CreatedFilterOptions[
                                key as keyof typeof CreatedFilterOptions
                              ].display
                            }
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center gap-2">
                    <X
                      className="size-4 cursor-pointer"
                      onClick={() => setStartDate(null)}
                    />
                  </div>
                </div>
              )}

              {createdBy && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-2 w-30">
                    <UserRound size={14} />
                    Created by
                  </div>

                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                        {data.projectCreators.find((c) => c.id === createdBy)
                          ?.fullName || "Select Creator"}
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground max-h-44"
                      style={{
                        width: "var(--radix-dropdown-menu-trigger-width)",
                      }}
                      align="end"
                      onCloseAutoFocus={(e) => e.preventDefault()}
                      onInteractOutside={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest(".creator-search-input"))
                          e.preventDefault();
                      }}
                      onPointerDownOutside={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest(".creator-search-input"))
                          e.preventDefault();
                      }}
                    >
                      <div className="p-2">
                        <input
                          type="text"
                          placeholder="Search creator..."
                          className="creator-search-input w-full border rounded px-2 py-1 text-sm"
                          value={creatorSearch}
                          onChange={(e) => setCreatorSearch(e.target.value)}
                          onKeyDownCapture={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUpCapture={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                        />
                      </div>

                      {data.projectCreators
                        .filter((creator) => createdBy !== creator.id)
                        .filter((creator) => {
                          const search = creatorSearch.toLowerCase();
                          const name = creator.fullName?.toLowerCase() || "";
                          const email = creator.email?.toLowerCase() || "";
                          return (
                            name.includes(search) || email.includes(search)
                          );
                        })
                        .sort((a, b) => {
                          const nameA = (
                            a.fullName?.trim() ||
                            a.email ||
                            ""
                          ).toLowerCase();
                          const nameB = (
                            b.fullName?.trim() ||
                            b.email ||
                            ""
                          ).toLowerCase();
                          return nameA.localeCompare(nameB, undefined, {
                            sensitivity: "base",
                          });
                        })
                        .map((creator) => (
                          <DropdownMenuItem
                            key={creator.id}
                            className="cursor-pointer"
                            onClick={() => {
                              setCreatedBy(creator.id);
                              setCreatorSearch("");
                            }}
                          >
                            {creator.fullName?.trim() || (
                              <LongContentWrapper>
                                {creator.email}
                              </LongContentWrapper>
                            )}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center gap-2">
                    <X
                      className="size-4 cursor-pointer"
                      onClick={() => setCreatedBy(null)}
                    />
                  </div>
                </div>
              )}

              {!!showOfficeFilter && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 pl-2 w-30">
                    <UserRound size={14} />
                    Select Office
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="flex-1 border justify-between text-secondary-foreground font-normal">
                        {officeIdSelected.length > 0
                          ? `${officeIdSelected.length} Office${officeIdSelected.length > 1 ? "s" : ""} Selected`
                          : "All Offices"}
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                      className="text-secondary-foreground max-h-52 w-[var(--radix-dropdown-menu-trigger-width)]"
                      align="end"
                    >
                      <div className="p-2 w-full flex flex-col items-end">
                        <span
                          className="text-xs text-end cursor-pointer text-paint hover:underline"
                          onClick={() => setOfficeIdSelected([])}
                        >
                          Clear
                        </span>

                        <div className="relative w-full">
                          <input
                            type="text"
                            placeholder="Search office..."
                            className="office-search-input w-full border rounded px-2 py-1 pr-5 text-sm"
                            value={officeSearch}
                            onChange={(e) => setOfficeSearch(e.target.value)}
                            onKeyDownCapture={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUpCapture={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                          />
                          <button
                            type="button"
                            aria-label="Clear search"
                            className="absolute right-1 top-2 w-4 h-4 flex items-center justify-center bg-transparent border-none p-0 m-0 cursor-pointer"
                            onClick={() => setOfficeSearch("")}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {[...(data.projectOffices || [])]
                        .filter((office) =>
                          office.name
                            .toLowerCase()
                            .includes(officeSearch.toLowerCase()),
                        )
                        .sort((a, b) =>
                          (a.name || "").localeCompare(
                            b.name || "",
                            undefined,
                            {
                              sensitivity: "base",
                            },
                          ),
                        )
                        .map((office) => (
                          <DropdownMenuItem
                            key={office.id}
                            className={cn(
                              "cursor-pointer flex items-center gap-2 justify-between",
                            )}
                            onClick={(e) => {
                              e.preventDefault();

                              setOfficeIdSelected((prev) =>
                                prev.includes(office.id)
                                  ? prev.filter((id) => id !== office.id)
                                  : [...prev, office.id],
                              );
                            }}
                          >
                            {office.name}

                            <Checkbox
                              checked={officeIdSelected.includes(office.id)}
                              aria-label={`Select ${office.name}`}
                            />
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center gap-2">
                    <X
                      className="size-4 cursor-pointer"
                      onClick={() => {
                        setOfficeIdSelected([]);
                        setShowOfficeFilter(false);
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <DropdownMenu
                  onOpenChange={(open) => setIsAddFiltersOpen(open)}
                >
                  {(!createdBy ||
                    !startDate ||
                    !projectType ||
                    !showOfficeFilter) && (
                    <DropdownMenuTrigger asChild>
                      <Button
                        className={cn(
                          "bg-tertiary text-secondary-foreground w-fit text-xs",
                          {
                            "text-primary-foreground": isAddFiltersOpen,
                          },
                        )}
                      >
                        <Plus />
                        Add filter
                        {isAddFiltersOpen ? <ChevronUp /> : <ChevronDown />}
                      </Button>
                    </DropdownMenuTrigger>
                  )}

                  <DropdownMenuContent
                    className="text-secondary-foreground w-50"
                    align="start"
                  >
                    {data.projectCreators.length > 0 &&
                      !createdBy &&
                      (isHotspringVendorProfile ||
                        role === CompanyRole.Client) && (
                        <DropdownMenuItem
                          className="cursor-pointer"
                          onClick={() => {
                            setCreatedBy(data.projectCreators[0].id);
                          }}
                        >
                          <UserRound />
                          Created By
                        </DropdownMenuItem>
                      )}

                    {!startDate && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => setStartDate("LAST_WEEK")}
                      >
                        <Calendar />
                        Created On
                      </DropdownMenuItem>
                    )}

                    {!projectType && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                          setProjectType(ProjectCategoryType.Tv);
                        }}
                      >
                        <Files className="rotate-y-180" />
                        Project Type
                      </DropdownMenuItem>
                    )}

                    {!showOfficeFilter && (
                      <DropdownMenuItem
                        className="cursor-pointer"
                        onClick={() => {
                          setShowOfficeFilter(true);
                          setOfficeIdSelected([]);
                        }}
                      >
                        <Landmark />
                        Select Offices
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div>
                  {isHotspringVendorProfile &&
                    isAdminOrCollaborator(currentUser?.role) && (
                      <div className="flex items-center border-1 p-2 rounded-lg gap-2">
                        <Lock className="size-3" />
                        <span>Archived</span>
                        <Switch.Root
                          checked={showArchived}
                          onCheckedChange={() => setShowArchived(!showArchived)}
                          className="
                            relative inline-flex items-center w-8 h-4 
                            bg-[#F0F0F0] dark:bg-black 
                            rounded-[12px] p-[2px] 
                            transition-colors duration-200 ease-in-out
                            data-[state=checked]:bg-paint!
                          "
                        >
                          <Switch.Thumb
                            className="
                              block w-[12px] h-[12px] rounded-full 
                              bg-[#FF744D] shadow-[0px_1px_2px_0px_#1018280F,_0px_1px_3px_0px_#1018281A] 
                              transition-all duration-200 ease-in-out
                              translate-x-0 data-[state=checked]:translate-x-4
                              data-[state=checked]:bg-white
                            "
                          />
                        </Switch.Root>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>

          <Separator className="mt-5 mb-3" />

          <div className="flex items-center gap-4 w-fit ml-auto mr-0">
            <Button
              onClick={() => {
                setIsFilterModalOpen(false);
              }}
            >
              Cancel
            </Button>

            <Button
              className="bg-service-paint text-white hover:bg-service-paint/96"
              onClick={() => {
                applyFilters();
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SearchBar() {
  const [searchText, setSearchText] = useDebouncedSearchParam(
    "projectName",
    300,
  );

  return (
    <div className="flex relative items-center gap-2 p-2 bg-primary border rounded-sm h-[38px]">
      <SearchIcon
        color="var(--color-secondary-foreground)"
        className="w-3.5 h-3.5"
      />

      <Input
        type="text"
        placeholder="Search Projects"
        className="border-none bg-transparent! placeholder:text-sm p-0 py-0.3 w-40 text-sm pr-5 shadow-none"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />

      {searchText && (
        <X
          className="absolute right-2 h-4 w-4 cursor-pointer"
          onClick={() => {
            setSearchText("");
          }}
        />
      )}
    </div>
  );
}

export enum ProjectFilterOptions {
  SHOW_ARCHIVED = "showArchived",
  SHOW_SLAPSHOT_REQUESTED = "showSlapshotRequested",
  SHOW_COMPLETED = "showCompleted",
  SHOW_UNASSIGNED = "showUnassigned",
  PROJECT_TYPE = "projectType",
  START_DATE = "startDate",
  CREATED_BY = "createdBy",
  OFFICE_ID = "officeId",
}

export const CreatedFilterOptions = {
  LAST_WEEK: { date: addDays(new Date(), -7), display: "Last Week" },
  LAST_MONTH: { date: addDays(new Date(), -30), display: "Last Month" },
  LAST_3_MONTHS: { date: addDays(new Date(), -90), display: "Last 3 Months" },
  LAST_6_MONTHS: { date: addDays(new Date(), -180), display: "Last 6 Months" },
  LAST_YEAR: { date: addDays(new Date(), -365), display: "Last Year" },
} as const;
